// Typescript wrapper for https://github.com/The-Lambda-Group/query-omega-oql/blob/development/oql/app/public/api/page/view.oql

import { debounce } from "lodash";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addDatabaseView,
  deleteDatabaseView,
  editDatabaseView,
  getDatabaseViews,
  swapDatabaseViews,
} from "../../api/page/view";
import { useAppId } from "../../hook";
import { ActionType, AppDispatch, RootState } from "../../reducer";
import {
  SetPagePageViewsAction,
  SetSelectedViewIdAction,
  SwapViewsAction,
} from "../../reducer/note/view";
import { PageView, getViewPropId } from "../../types/notebook/view";
import { useDatabasePages, useGetNotebookPage } from "../page";
import { useFolderPropertyTypes } from "./property";

export const useFetchDatabaseViews: () => (config: {
  pageId: string;
}) => Promise<any> = () => {
  const [appId] = useAppId();
  const dispatch = useDispatch();

  return async ({ pageId }) => {
    if (!appId) {
      throw new Error("No App Id.");
    }

    return getDatabaseViews({ "page-id": pageId, "app-id": appId }).then(
      (pageViews) => {
        const action: SetPagePageViewsAction = {
          type: ActionType.SET_PAGE_PAGE_VIEWS,
          pageId,
          pageViews,
        };
        dispatch(action as any);
      },
    );
  };
};

export const useDatabaseViews: (pageId?: string) => PageView[] = (pageId) => {
  const pageViews = useSelector((state: RootState) => {
    if (!pageId) return [];
    const pageToPageViews = state.ui.pageToPageViews;
    return pageId in pageToPageViews ? pageToPageViews[pageId] : [];
  });

  return pageViews;
};

export const useAddDatabaseView: () => (
  view: PageView,
) => Promise<any> = () => {
  const fetchDatabaseViews = useFetchDatabaseViews();
  const [appId] = useAppId();
  return async (view) => {
    if (!appId) {
      throw new Error("No App Id!");
    }

    return addDatabaseView(view).then((res) => {
      fetchDatabaseViews({ pageId: view["page-id"] });
    });
  };
};

export const useDeleteDatabaseView: () => (config: {
  viewId: string;
  pageId: string;
}) => Promise<any> = () => {
  const [viewId, setViewId] = useSelectedDatabaseViewId();
  const fetchDatabaseViews = useFetchDatabaseViews();
  const [appId] = useAppId();
  return async ({ viewId, pageId }) => {
    if (!appId) {
      throw new Error("No App Id!");
    }

    return deleteDatabaseView({ "app-id": appId, "view-id": viewId }).then(
      (res) => {
        fetchDatabaseViews({ pageId });
        setViewId(undefined);
      },
    );
  };
};

export const useDatabaseView: (viewId?: string) => PageView | undefined = (
  viewId,
) => {
  const pageView = useSelector((state: RootState) =>
    viewId ? state.ui.pageViews[viewId] : undefined,
  );
  return pageView;
};

export const useSelectedDatabaseViewId: () => [
  string | undefined,
  (viewId: string | undefined) => void,
] = () => {
  const dispatch = useDispatch();
  const setSelectedDatabaseView = (viewId?: string) => {
    const action: SetSelectedViewIdAction = {
      viewId,
      type: ActionType.SET_SELECTED_VIEW_ID,
    };
    dispatch(action as any);
  };
  const selectedDatabaseView = useSelector(
    (state: RootState) => state.ui.selectedViewId,
  );
  return [selectedDatabaseView, setSelectedDatabaseView];
};

export const useSelectedDatabaseView: () => PageView | undefined = () => {
  const selectedDatabaseView = useSelector((state: RootState) => {
    const { selectedViewId: viewId, pageViews } = state.ui;
    return viewId && viewId in pageViews ? pageViews[viewId] : undefined;
  });
  return selectedDatabaseView;
};

export const useSetDatabaseView: () => (config: {
  view: PageView;
  pageId: string;
}) => void = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [appId] = useAppId();

  const debouncedWrite = useMemo(
    () =>
      debounce((view: PageView) => {
        if (!appId) return;
        editDatabaseView(view);
      }, 1000),
    [appId],
  );

  return ({ view, pageId }) => {
    const pageViews = [view];
    const action: SetPagePageViewsAction = {
      type: ActionType.SET_PAGE_PAGE_VIEWS,
      pageId,
      pageViews,
    };
    debouncedWrite(view);
    dispatch(action as any);
  };
};

export enum MoveDirection {
  LEFT = "LEFT",
  DOWN = "DOWN",
  RIGHT = "RIGHT",
  UP = "UP",
}

export const useHandleViewTab: (
  viewId: string | undefined,
  folderId: string,
) => (config: {
  direction?: MoveDirection;
  rowIndex: number;
  columnIndex: number;
}) => void = (viewId, folderId) => {
  const view = useDatabaseView(viewId);
  const getPage = useGetNotebookPage();
  const folder = getPage(folderId);
  const { folderProps } = useFolderPropertyTypes(folderId);
  const props = Object.values(folderProps ?? {});
  const propLength = props.length;
  const pages = useDatabasePages(folderId);

  return ({ direction, rowIndex, columnIndex }) => {
    // const numCols = propLength + 1;
    // const numRows = pages?.length ?? 0;
    // const maxIndex = numCols * numRows;
    // const selectedIndex = rowIndex * numCols + columnIndex;
    // const offset =
    //   direction === MoveDirection.DOWN
    //     ? numCols
    //     : direction === MoveDirection.UP
    //       ? -numCols
    //       : direction === MoveDirection.RIGHT
    //         ? 1
    //         : direction === MoveDirection.LEFT
    //           ? -1
    //           : 0;
    // const newIndex = Math.min(Math.max(selectedIndex + offset, 0), maxIndex);
    // const newRowIndex = Math.floor(newIndex / numCols);
    // const newColumnIndex = newIndex % numCols;
    // const pageId =
    //   newIndex === maxIndex ? "add-page" : pages[newRowIndex]?.["page-id"]!;
    // const propId =
    //   newIndex === maxIndex
    //     ? "add-page"
    //     : newColumnIndex === 0
    //       ? "page-name"
    //       : propIds?.[newColumnIndex - 1]!;
    // // console.log(
    // //   JSON.stringify(
    // //     {
    // //       maxIndex,
    // //       pageId,
    // //       propId,
    // //       newIndex,
    // //       selectedIndex,
    // //       newRowIndex,
    // //       newColumnIndex,
    // //     },
    // //     null,
    // //     4
    // //   )
    // // );
    // const viewPropId = getViewPropId({ pageId, propId });
    // const elem = document.getElementById(viewPropId);
    // elem?.focus();
  };
};

export const useSwapDatabaseViews: () => (config: {
  active: PageView;
  over: PageView;
}) => void = () => {
  const dispatch = useDispatch<AppDispatch>();

  return ({ active, over }) => {
    const pageId = active["page-id"];
    const appId = active["app-id"];
    const action: SwapViewsAction = {
      active,
      over,
      type: ActionType.SWAP_DATABASE_VIEWS,
    };
    dispatch(action as any);
    swapDatabaseViews({
      "page-id": pageId,
      "app-id": appId,
      "active-view-id": active["view-id"]!,
      "over-view-id": over["view-id"]!,
    });
  };
};
